import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  useMediaQuery,
} from "@mui/material";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { getUpcommingAuction } from "../../Model/Auction";
import AboutUSs from "./../../images/resources/howtobuy.png";

const HowToBuy = () => {
  const [auctionData, setAuctionData] = useState({
    title: "",
    details: "",
    catalog: "",
  });

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:1024px)");
  const isDesktop = !isTablet;

  useEffect(() => {
    const fetchAuction = async () => {
      try {
        const results = await getUpcommingAuction();

        if (results && results.length > 0) {
          setAuctionData({
            catalog: results[0].cover,
            title: results[0].d_name,
            details: results[0].d_desc,
          });
        }
      } catch (error) {
        console.error("Error fetching auction:", error);
      }
    };

    fetchAuction();
  }, []);

  const steps = [
    {
      number: "01",
      icon: <MenuBookOutlinedIcon />,
      title: "Browse",
      description:
        "Stay informed about upcoming sales by visiting our website or downloading our digital catalogue. Explore detailed provenance, condition reports, and artist histories. We highly encourage interested buyers to attend our pre-auction viewings held at the gallery typically a week before the main event to inspect pieces in person.",
    },
    {
      number: "02",
      icon: <HowToRegOutlinedIcon />,
      title: "Register & Bid",
      description:
        "To participate in person, please register at our front desk. Ensure you bring a valid government-issued ID to secure your bidding paddle. We recommend new clients to complete registration at least 48 hours prior to the auction to ensure a seamless experience.",
    },
  ];

  const biddingMethods = [
    {
      icon: <LanguageOutlinedIcon />,
      title: "ONLINE",
      description: (
        <>
          Experience the auction in real-time from anywhere in the world via
          <a
            href="https://www.leonexchange.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#fff",
              fontWeight: 600,
              textDecoration: "underline",
              marginLeft: "6px",
            }}
          >
            Leon Exchange
          </a>
          .
        </>
      ),
    },
    {
      icon: <PhoneInTalkOutlinedIcon />,
      title: "TELEPHONE",
      description:
        "Our staff can call you directly when your lot is about to be offered, allowing you to bid live over the phone with an agent.",
    },
    {
      icon: <AssignmentOutlinedIcon />,
      title: "ABSENTEE",
      description:
        "Submit your maximum bid in advance, and our auctioneer will execute it on your behalf at the lowest possible price.",
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        color: "#111",
        minHeight: "100vh",
      }}
    >
      {/* HERO */}
      <Box
        component="section"
        sx={{
          backgroundImage: `
            linear-gradient(
              rgba(0,0,0,0.38),
              rgba(0,0,0,0.48)
            ),
            url(${AboutUSs})
          `,
          backgroundColor: "#111",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: isDesktop ? "fixed" : "scroll",
          minHeight: isMobile ? "360px" : "520px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "#fff",
          px: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: isMobile ? "11px" : "13px",
              letterSpacing: isMobile ? "3px" : "5px",
              textTransform: "uppercase",
              mb: 2,
              opacity: 0.9,
            }}
          >
            León Gallery
          </Typography>

          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: isMobile ? "38px" : "72px",
              lineHeight: 1,
              fontWeight: 400,
              letterSpacing: "-1px",
              mb: 3,
            }}
          >
            How to Buy
          </Typography>

          <Box
            sx={{
              width: "45px",
              height: "1px",
              bgcolor: "#fff",
              mx: "auto",
              mb: 3,
              opacity: 0.7,
            }}
          />

          <Typography
            sx={{
              fontSize: isMobile ? "13px" : "15px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              opacity: 0.9,
            }}
          >
            A Collector's Guide
          </Typography>
        </Box>
      </Box>

      {/* INTRO / SHORTCUTS */}
      <Container
        maxWidth="lg"
        sx={{
          py: isMobile ? 6 : 9,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 3,
            mb: 6,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "3px",
                textTransform: "uppercase",
                color: "#888",
                mb: 1.5,
              }}
            >
              A simple guide
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? "30px" : "42px",
                fontWeight: 400,
                lineHeight: 1.1,
              }}
            >
              Your journey to collecting
            </Typography>
          </Box>

          {auctionData.title && (
            <Typography
              sx={{
                fontSize: "13px",
                color: "#777",
                maxWidth: "300px",
              }}
            >
          
            </Typography>
          )}
        </Box>

        {/* QUICK LINKS */}
        <Box
          sx={{
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : "repeat(3, 1fr)",
            mb: 9,
          }}
        >
          {[
            ["01", "Browse", "#browse"],
            ["02", "Register & Bid", "#register"],
            ["03", "Bidding Methods", "#methods"],
          ].map(([number, label, href]) => (
            <Box
              key={href}
              component="a"
              href={href}
              sx={{
                textDecoration: "none",
                color: "#111",
                py: 2.5,
                px: 2,
                borderRight: "1px solid #ddd",
                "&:hover": {
                  bgcolor: "#f7f7f7",
                },
                transition: "0.2s ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#999",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                {number}
              </Typography>

              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* STEPS */}
        <Box id="browse">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 5,
            }}
          >
            <GavelOutlinedIcon
              sx={{
                fontSize: 30,
                fontWeight: 300,
              }}
            />

            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "3px",
                textTransform: "uppercase",
                color: "#777",
              }}
            >
              Before the auction
            </Typography>
          </Box>

          <Grid container spacing={isMobile ? 5 : 10}>
            {steps.map((step) => (
              <Grid item xs={12} md={6} key={step.number}>
                <Box
                  sx={{
                    borderTop: "1px solid #222",
                    pt: 3,
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        border: "1px solid #222",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {step.icon}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#999",
                        letterSpacing: "1px",
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: isMobile ? "28px" : "34px",
                      fontWeight: 400,
                      mb: 2,
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "15px",
                      lineHeight: 1.9,
                      color: "#555",
                      maxWidth: "560px",
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* BIDDING METHODS */}
        <Box
          id="methods"
          sx={{
            mt: isMobile ? 9 : 13,
            bgcolor: "#111",
            color: "#fff",
            px: isMobile ? 3 : 7,
            py: isMobile ? 5 : 7,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 5,
            }}
          >
            <GavelOutlinedIcon sx={{ fontSize: 30 }} />

            <Box>
              <Typography
                sx={{
                  fontSize: "10px",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  color: "#aaa",
                }}
              >
                Choose your method
              </Typography>

              <Typography
                sx={{
                  fontSize: isMobile ? "28px" : "38px",
                  fontWeight: 400,
                }}
              >
                Bidding Methods
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={0}>
            {biddingMethods.map((method, index) => (
              <Grid
                item
                xs={12}
                md={4}
                key={method.title}
                sx={{
                  borderTop: "1px solid #444",
                  borderRight:
                    !isMobile && index !== 2
                      ? "1px solid #444"
                      : "none",
                  p: isMobile ? 3 : 4,
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    border: "1px solid #777",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  {method.icon}
                </Box>

                <Typography
                  sx={{
                    fontSize: "14px",
                    letterSpacing: "2px",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  {method.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "14px",
                    lineHeight: 1.8,
                    color: "#bbb",
                  }}
                >
                  {method.description}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* REGISTER */}
        <Box
          id="register"
          sx={{
            mt: isMobile ? 8 : 12,
            borderTop: "1px solid #222",
            borderBottom: "1px solid #222",
            py: isMobile ? 5 : 7,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "3px",
                color: "#888",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Ready to participate?
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? "28px" : "36px",
                fontWeight: 400,
              }}
            >
              Explore the auctions
            </Typography>
          </Box>

          <Box
            component="a"
            href="/auctions"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              color: "#111",
              textDecoration: "none",
              border: "1px solid #111",
              px: 3,
              py: 1.8,
              fontSize: "12px",
              letterSpacing: "1.5px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              transition: "all 0.25s ease",
              "&:hover": {
                bgcolor: "#111",
                color: "#fff",
              },
            }}
          >
            VIEW ALL AUCTIONS
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowToBuy;